import PropTypes from 'prop-types'
import React from 'react'
import { Redirect, Route } from 'react-router-dom'

import { navigationPaths } from 'app/config'
import { isUserAuthenticated } from 'app/helpers'

/**
 * Route guard — AUTHENTICATION ONLY.
 *
 * A Role decides what Alfred *shows* in the sidebar (pruneMenusByAccess in
 * MainLayout), not what a user may open. Per Role's own docstring this is a UI
 * hint, NOT an authorization boundary, and the backend never checks it either —
 * so hitting a page's URL directly is allowed even when the role hides its tab.
 *
 * This guard used to render a "You don't have access to this section" screen
 * when the role didn't cover the page. That turned the sidebar hint into a hard
 * lockout: e.g. an Admissions Coordinator opening /support/interaction-history
 * directly (or via a deep link with ?openModalForm=1 to log an interaction /
 * create a visitor) got blocked. Tabs are still hidden in the sidebar; the URL
 * stays reachable.
 *
 * `permissionId` is still accepted so call sites need no change; it no longer
 * gates the route. It is destructured out here purely to keep it from being
 * spread onto the DOM <Route>.
 */
const PrivateRoute = (props) => {
  // eslint-disable-next-line no-unused-vars
  const { component: Component, permissionId, ...rest } = props

  return (
    <Route
      {...rest}
      render={({ location, ...matchProps }) => {
        if (!isUserAuthenticated()) {
          return (
            <Redirect
              to={{
                pathname: navigationPaths.login,
                state: { from: location }
              }}
            />
          )
        }

        return <Component {...matchProps} />
      }}
    />
  )
}

PrivateRoute.propTypes = {
  component: PropTypes.any.isRequired,
  path: PropTypes.string,
  // Accepted for call-site compatibility; no longer gates the route (roles only
  // hide sidebar tabs).
  permissionId: PropTypes.string
}

export default PrivateRoute
